import { create } from 'zustand';

const ACCESS_TOKEN_KEY = 'duzon_access_token';
const SOCIAL_SUB_KEY = 'duzon_social_sub';
const REMEMBER_KEY = 'duzon_remember_me';
const SESSION_EXPIRES_KEY = 'duzon_session_expires_at';

interface AuthState {
  accessToken: string | null;
  socialSub: string | null;
  rememberMe: boolean;
  sessionExpiresAt: number | null;
  isAuthenticated: boolean;
  isHydrated: boolean;
  setTokens: (payload: {
    accessToken: string;
    socialSub?: string | null;
    rememberMe?: boolean;
    sessionExpiresAt?: number | null;
  }) => void;
  setAccessToken: (accessToken: string, sessionExpiresAt?: number | null, rememberMe?: boolean) => void;
  setRememberMe: (rememberMe: boolean) => void;
  setHydrated: (hydrated: boolean) => void;
  clearAuth: () => void;
}

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key) ?? sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeAccess(accessToken: string, _rememberMe: boolean) {
  try {
    // 결재 상세 등 window.open 새 창과 공유되도록 access는 항상 localStorage에 둔다.
    // (로그인 유지는 refresh 쿠키 TTL로만 구분)
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  } catch {
    // ignore
  }
}

function writeSocialSub(socialSub: string | null, _rememberMe: boolean) {
  try {
    localStorage.removeItem(SOCIAL_SUB_KEY);
    sessionStorage.removeItem(SOCIAL_SUB_KEY);
    if (!socialSub) return;
    localStorage.setItem(SOCIAL_SUB_KEY, socialSub);
  } catch {
    // ignore
  }
}

function writeSessionExpires(sessionExpiresAt: number | null, _rememberMe: boolean) {
  try {
    localStorage.removeItem(SESSION_EXPIRES_KEY);
    sessionStorage.removeItem(SESSION_EXPIRES_KEY);
    if (sessionExpiresAt == null) return;
    localStorage.setItem(SESSION_EXPIRES_KEY, String(sessionExpiresAt));
  } catch {
    // ignore
  }
}

const initialRemember = readStorage(REMEMBER_KEY) === '1';
const initialAccessToken = readStorage(ACCESS_TOKEN_KEY);
const initialSocialSub = readStorage(SOCIAL_SUB_KEY);
const initialExpiresRaw = readStorage(SESSION_EXPIRES_KEY);
const initialSessionExpiresAt = initialExpiresRaw ? Number(initialExpiresRaw) : null;

export const useAuthStore = create<AuthState>((set, get) => ({
  accessToken: initialAccessToken,
  socialSub: initialSocialSub,
  rememberMe: initialRemember,
  sessionExpiresAt:
    initialSessionExpiresAt != null && !Number.isNaN(initialSessionExpiresAt)
      ? initialSessionExpiresAt
      : null,
  isAuthenticated: Boolean(initialAccessToken),
  isHydrated: false,

  setRememberMe: (rememberMe) => {
    try {
      localStorage.setItem(REMEMBER_KEY, rememberMe ? '1' : '0');
    } catch {
      // ignore
    }
    set({ rememberMe });
  },

  setTokens: ({ accessToken, socialSub, rememberMe, sessionExpiresAt }) => {
    const remember = rememberMe ?? get().rememberMe;
    const expires =
      sessionExpiresAt === undefined ? get().sessionExpiresAt : sessionExpiresAt;
    writeAccess(accessToken, remember);
    if (socialSub !== undefined) {
      writeSocialSub(socialSub, remember);
    }
    writeSessionExpires(expires ?? null, remember);
    try {
      localStorage.setItem(REMEMBER_KEY, remember ? '1' : '0');
      localStorage.removeItem('duzon_refresh_token');
      sessionStorage.removeItem('duzon_refresh_token');
    } catch {
      // ignore
    }
    set({
      accessToken,
      socialSub: socialSub === undefined ? get().socialSub : socialSub,
      rememberMe: remember,
      sessionExpiresAt: expires ?? null,
      isAuthenticated: true,
    });
  },

  setAccessToken: (accessToken, sessionExpiresAt, rememberMe) => {
    const remember = rememberMe ?? get().rememberMe;
    writeAccess(accessToken, remember);
    if (sessionExpiresAt !== undefined) {
      writeSessionExpires(sessionExpiresAt, remember);
    }
    set({
      accessToken,
      isAuthenticated: true,
      rememberMe: remember,
      sessionExpiresAt:
        sessionExpiresAt === undefined ? get().sessionExpiresAt : sessionExpiresAt,
    });
  },

  setHydrated: (hydrated) => set({ isHydrated: hydrated }),

  clearAuth: () => {
    try {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      sessionStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(SOCIAL_SUB_KEY);
      sessionStorage.removeItem(SOCIAL_SUB_KEY);
      localStorage.removeItem(SESSION_EXPIRES_KEY);
      sessionStorage.removeItem(SESSION_EXPIRES_KEY);
      localStorage.removeItem('duzon_refresh_token');
      sessionStorage.removeItem('duzon_refresh_token');
    } catch {
      // ignore
    }
    set({
      accessToken: null,
      socialSub: null,
      sessionExpiresAt: null,
      isAuthenticated: false,
    });
  },
}));
